import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Users, Clock, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';


const Languages = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const languages = [
    {
      name: t('nameen'),
      flag: '🇬🇧',
      certification: t('certificationen'),
      levels: 'A1 - C2',
      students: '180+',
      color: 'from-blue-500 to-blue-700',
      features: t('featuresen')
    },
    {
      name: t('namefr'),
      flag: '🇫🇷',
      certification: t('certificationfr'),
      levels: 'A1 - C2',
      students: '120+',
      color: 'from-red-500 to-red-700',
      features: t('featuresfr')
    },
    {
      name: t('namees'),
      flag: '🇪🇸',
      certification: t('certificationes'),
      levels: 'A1 - C2',
      students: '95+',
      color: 'from-yellow-500 to-orange-600',
      features: t('featureses')
    },
    {
      name: t('namede'),
      flag: '🇩🇪',
      certification: t('certificationde'),
      levels: 'A1 - C2',
      students: '75+',
      color: 'from-gray-600 to-gray-800',
      features: t('featuresde')
    },
    {
      name: t('nameit'),
      flag: '🇮🇹',
      certification: t('certificationit'),
      levels: 'A1 - C2',
      students: '0',
      color: 'from-green-500 to-green-700',
      features: t('featuresit')
    },
    {
      name: t('namept'),
      flag: '🇵🇹',
      certification:  t('certificationpt'),
      levels: 'A1 - C2',
      students: '0',
      color: 'from-purple-500 to-purple-700',
      features:   t('featurespt')
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="languages" className="py-20 bg-gradient-to-br from-neutral-50 to-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-accent-500/10 text-accent-500 mb-6">
            <Award className="w-5 h-5 mr-2" />
            <span className="font-medium">{t('languagesCerts')}</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold font-heading text-primary-500 mb-6">
             {t('languagesTitle')}
            <span className="block text-accent-500">{t('languagesSubtitle')}</span>
          </h2>
          
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            {t('languagesDescription')}
          </p>
        </motion.div>

        {/* Languages Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {languages.map((language, index) => (
            <motion.div
              key={language.name}
              variants={itemVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-neutral-100 group"
            >
              {/* Language Header */}
              <div className="text-center mb-6">
                <div className="text-6xl mb-4 animate-float">{language.flag}</div>
                <h3 className="text-2xl font-bold text-primary-500 mb-2 group-hover:text-accent-500 transition-colors">
                  {language.name}
                </h3>
                <div className={`inline-block px-4 py-2 rounded-full bg-gradient-to-r ${language.color} text-white text-sm font-medium`}>
                  {language.certification}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-3 bg-neutral-50 rounded-2xl">
                  <div className="text-2xl font-bold text-primary-500">{language.levels}</div>
                  <div className="text-sm text-neutral-600">{t('levels')}</div>
                </div>
                <div className="text-center p-3 bg-neutral-50 rounded-2xl">
                  <div className="text-2xl font-bold text-primary-500">{language.students}</div>
                  <div className="text-sm text-neutral-600">{t('students')}</div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3">
                {language.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-accent-500 flex-shrink-0" />
                    <span className="text-neutral-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <motion.button
                className="w-full mt-6 bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-2xl transition-colors duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => window.location.href = "#contact"}
              >
                 {t('learnmore')}
              </motion.button>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-primary rounded-3xl p-8 lg:p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Not Sure Which Language to Choose?
            </h3>
            <p className="text-xl mb-8 opacity-90">
              Take our free assessment to find the perfect language path for your child's future.
            </p>
            <motion.a
              href="#contact"
              className="btn-primary bg-accent-500 hover:bg-accent-600"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('takeFreeAssessment')}
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Languages;
